import React, { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useEmpresa } from "@/contexts/EmpresaContext";
import type { Profissional, ModeloAcesso } from "@/hooks/useTeam";
import type { PermissoesMap, Modulo } from "@/hooks/useTeamPermissions";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

interface ProfissionalContextType {
  profissional: Profissional | null;
  permissoes: PermissoesMap | null;
  modeloAcesso: ModeloAcesso | "owner";
  /** Usuário não tem registro em profissionais — é owner/dono da empresa */
  isOwner: boolean;
  /** Profissional com modelo_acesso = "admin" */
  isAdmin: boolean;
  /** Profissional com modelo_acesso = "profissional" */
  isProfissional: boolean;
  /** Profissional com modelo_acesso = "recepcionista" */
  isRecepcionista: boolean;
  /** isOwner ou isAdmin — pode ver todos os dados */
  podeVerTudo: boolean;
  isLoading: boolean;
  podeVer: (modulo: Modulo) => boolean;
  podeCriar: (modulo: Modulo) => boolean;
  podeEditar: (modulo: Modulo) => boolean;
  podeExcluir: (modulo: Modulo) => boolean;
}

const ProfissionalContext = createContext<ProfissionalContextType>({
  profissional: null,
  permissoes: null,
  modeloAcesso: "owner",
  isOwner: true,
  isAdmin: false,
  isProfissional: false,
  isRecepcionista: false,
  podeVerTudo: true,
  isLoading: false,
  podeVer: () => true,
  podeCriar: () => true,
  podeEditar: () => true,
  podeExcluir: () => true,
});

export function ProfissionalProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { empresaId, isLoading: isEmpresaLoading, empresas } = useEmpresa();

  const profQuery = useQuery({
    queryKey: ["profissional-atual", user?.id, empresaId],
    queryFn: async () => {
      if (!user?.id || !empresaId) return null;
      const { data } = await db
        .from("profissionais")
        .select("*")
        .eq("user_id", user.id)
        .eq("empresa_id", empresaId)
        .maybeSingle();
      return (data ?? null) as Profissional | null;
    },
    enabled: !!user?.id && !!empresaId,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const profissional = profQuery.data ?? null;

  // Fonte de verdade do papel na empresa ativa: user_empresa_vinculos.
  // Não podemos inferir "owner" pela ausência de row em `profissionais`
  // (RLS/race podem retornar null indevidamente e conceder acesso total).
  const vinculoQuery = useQuery({
    queryKey: ["vinculo-atual", user?.id, empresaId],
    queryFn: async () => {
      if (!user?.id || !empresaId) return null;
      const { data } = await db
        .from("user_empresa_vinculos")
        .select("modelo_acesso, profissional_id, ativo")
        .eq("user_id", user.id)
        .eq("empresa_id", empresaId)
        .eq("ativo", true)
        .maybeSingle();
      return (data ?? null) as { modelo_acesso: string | null; profissional_id: number | null; ativo: boolean } | null;
    },
    enabled: !!user?.id && !!empresaId,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const permQuery = useQuery({
    queryKey: ["permissoes-atual", user?.id, empresaId, profissional?.id],
    queryFn: async () => {
      if (!profissional?.id) return null;
      const { data } = await db
        .from("profissional_permissoes")
        .select("*")
        .eq("profissional_id", profissional.id);
      if (!data || data.length === 0) return null;
      const map: Partial<PermissoesMap> = {};
      for (const row of data) {
        map[row.modulo as Modulo] = row;
      }
      return map as PermissoesMap;
    },
    enabled: !!profissional?.id,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const permissoes = permQuery.data ?? null;

  // Fonte de verdade do papel: profissional/vínculo da empresa ativa.
  // Segurança: nunca conceder owner/admin só porque `profissionais` retornou null.
  // Durante carregamento, erro de RLS ou ausência de vínculo, o padrão é sem_acesso.
  const vinculoModelo = (vinculoQuery.data?.modelo_acesso ?? null) as ModeloAcesso | null;
  const modelo: ModeloAcesso | null =
    (profissional?.modelo_acesso as ModeloAcesso | null) ?? vinculoModelo ?? null;
  // `isPending` (não `isLoading`) é o flag correto aqui: uma query com `enabled: false`
  // (empresaId/profissional ainda não resolvidos) reporta `isLoading: false` mesmo sem
  // nunca ter buscado dados, porque `isLoading = isPending && isFetching`. Usar `isLoading`
  // abre uma janela onde `roleLoading` vira `false` com `profissional`/`vinculoModelo` ainda
  // `null` por falta de dados (não porque o usuário realmente não tem vínculo), fazendo
  // `isOwner` calcular errado e o PermissionGuard redirecionar donos legítimos por engano.
  const roleLoading = isEmpresaLoading || profQuery.isPending || vinculoQuery.isPending;
  const hasActiveVinculo = !!vinculoQuery.data?.ativo;
  const permissionsLoading = !!profissional?.id && permQuery.isPending;
  const isLegacyOwner = empresas.length === 0;
  const isOwner =
    !roleLoading &&
    !!user?.id &&
    !!empresaId &&
    profissional === null &&
    vinculoModelo === null &&
    (hasActiveVinculo || isLegacyOwner);
  const isAdmin = modelo === "admin";
  const isProfissional = modelo === "profissional";
  const isRecepcionista = modelo === "recepcionista";
  const podeVerTudo = isOwner || isAdmin;

  const podeVer = useMemo(
    () => (modulo: Modulo) => {
      if (podeVerTudo) return true;
      return permissoes?.[modulo]?.pode_ver ?? false;
    },
    [podeVerTudo, permissoes]
  );

  const podeCriar = useMemo(
    () => (modulo: Modulo) => {
      if (podeVerTudo) return true;
      return permissoes?.[modulo]?.pode_criar ?? false;
    },
    [podeVerTudo, permissoes]
  );

  const podeEditar = useMemo(
    () => (modulo: Modulo) => {
      if (podeVerTudo) return true;
      return permissoes?.[modulo]?.pode_editar ?? false;
    },
    [podeVerTudo, permissoes]
  );

  const podeExcluir = useMemo(
    () => (modulo: Modulo) => {
      if (podeVerTudo) return true;
      return permissoes?.[modulo]?.pode_excluir ?? false;
    },
    [podeVerTudo, permissoes]
  );

  return (
    <ProfissionalContext.Provider
      value={{
        profissional,
        permissoes,
        modeloAcesso: isOwner ? "owner" : (modelo ?? "sem_acesso"),
        isOwner,
        isAdmin,
        isProfissional,
        isRecepcionista,
        podeVerTudo,
        isLoading: roleLoading || permissionsLoading,
        podeVer,
        podeCriar,
        podeEditar,
        podeExcluir,
      }}
    >
      {children}
    </ProfissionalContext.Provider>
  );
}

export function useProfissionalAtual() {
  return useContext(ProfissionalContext);
}
